/**
 * 에러 보고 부터 에러 관련 util성 함수들까지 모아놓고 있습니다.
 * Sentry를 직접 호출하지 말고 해당 파일을 통해서 호출해주세요.
 * eslint 설정을 통해서 Sentry를 직접 호출하지 못하도록 막아놓았습니다.
 */
import {
  captureException as sentryCaptureException,
  captureMessage as sentryCaptureMessage,
  setUser as sentrySetUser,
} from '@sentry/nextjs';
import { inspect } from 'util';
import { ZodError } from 'zod';

import { isRedirectError as isNextRedirectError } from 'next/dist/client/components/redirect';
import { stringOrInspect } from './string';
import { ExclusiveEventHintOrCaptureContext } from '@sentry/core/build/types/utils/prepareEvent';
import { ApiError } from 'callabo-api/src/core/ApiError';
import { getErrorDetail } from 'libs/error';

export const isRedirectError = (error: unknown): boolean => {
  return isNextRedirectError(error);
};

export const createError = (
  name: string,
  message: string | Record<string, unknown>,
  option?: { cause?: unknown }
): Error => {
  const error = new Error(stringOrInspect(message), { cause: option?.cause });
  error.name = name;
  return error;
};

export const error = (
  name: string,
  message: string | Record<string, unknown>
): ((e: unknown) => Error) => {
  return (e: unknown) => createError(name, stringOrInspect(message), { cause: e });
};

const captureZodError = (
  exception: ZodError<unknown>,
  hint?: ExclusiveEventHintOrCaptureContext
): void => {
  console.error(exception, hint);
  sentryCaptureException(exception, {
    extra: {
      issues: exception.issues,
      inspected_issues: inspect(exception.issues),
      ...hint,
    },
  });
};

const captureApiError = (exception: ApiError, hint?: ExclusiveEventHintOrCaptureContext): void => {
  console.error(exception, hint);
  sentryCaptureException(exception, {
    extra: {
      ...hint,
      requestBody: exception.request.body,
      responseBody: getErrorDetail(exception),
    },
  });
};

type User =
  | {
      name?: string;
      username?: string;
      uid?: string;
      email?: string;
    }
  | { ip: string | null };

export const setUserToSentry = (user: User): void => {
  sentrySetUser(user);
};

export const captureError = (error: Error, hint?: ExclusiveEventHintOrCaptureContext): void => {
  if (error instanceof ZodError) {
    captureZodError(error, hint);
  } else if (error instanceof ApiError) {
    captureApiError(error, hint);
  } else {
    let hintJSON = '';
    try {
      if (hint) {
        hintJSON = JSON.stringify(hint, null, 2);
      }
    } catch (e) {
      // 안돼도 큰 문제는 아님
    }

    console.error('sentry', error, hintJSON);
    sentryCaptureException(error, hint);
  }
};

export const captureMessage = (
  message: string,
  hint?: ExclusiveEventHintOrCaptureContext
): void => {
  console.error(message);
  sentryCaptureMessage(message, hint);
};

const sentryHistoryMap = new Map<string, string>();
export const captureMessageOnce = (
  message: string,
  extra: Record<string, unknown>,
  key: string
): void => {
  if (sentryHistoryMap.has(key)) {
    return;
  }
  sentryHistoryMap.set(key, message);
  captureMessage(message, { extra });
};
